import { Send, Sparkles } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Field, Steps, NextStep, Callout, RoleBadge, pathFor,
  Diagram, MockSelect, MockField, MockButton, MockRow,
} from "../shared";

export default function FormsMarkDispatchedSection() {
  return (
    <div data-testid="guide-forms-mark-dispatched">
      <PageHeading
        icon={Send}
        title="Mark Dispatched"
        description="Tell the system that an approved, above-MD item has actually been sent upward (by email, courier, hand-delivered, etc.). This moves it to Sent Upward and stops the held-time clock."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="admin" />
        <span className="text-xs text-white/40">Done by the SDF PMO and SDF Board Secretary in practice; any General Admin or above can perform it.</span>
      </div>

      <Callout variant="info" title="In plain words">
        Once the SDF MD approves an item that has to go higher up, it sits in <span className="text-yellow-300">Awaiting Dispatch</span> waiting to be sent. "Mark Dispatched" is you saying <span className="text-white">"done — it's been sent"</span>, and recording where and when.
      </Callout>

      <Callout variant="info" title="Part of a bigger change (v2.5.0)">
        <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3 text-amber-300" /> Read the full story in <Link href={pathFor("upward-dispatch")} className="text-cyan-300 hover:underline">Upward Dispatch</Link>.</span>
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-sm text-white/65 space-y-1.5 ml-2">
          <li>After you've physically sent an item that was waiting in Awaiting Dispatch.</li>
          <li>You have a working-day target to do this — the held-time chip shows how you're tracking.</li>
        </ul>
      </Section>

      <Section title="What the form looks like">
        <Diagram
          frameLabel="MD Meetings → Awaiting Dispatch → Mark Dispatched"
          legend={[
            <>Pick the <span className="text-white">recipient</span> — who or where you sent it.</>,
            <>Enter the <span className="text-white">date sent</span> — the day you actually sent it.</>,
            <>Optionally note the <span className="text-white">channel</span> (email, courier, etc.) and a free-text note.</>,
            <>Click <span className="text-white">Mark Dispatched</span> to record it.</>,
          ]}
        >
          <MockRow>
            <MockSelect label="Recipient" value="EDGE MD Office" marker={1} />
            <MockField label="Date sent" value="14 Apr 2026" marker={2} />
            <MockField label="Channel / method" placeholder="Email" marker={3} />
          </MockRow>
          <MockButton label="Mark Dispatched" marker={4} />
        </Diagram>
      </Section>

      <Section title="Step-by-step">
        <Steps
          steps={[
            <>Open <span className="text-white">MD Meetings</span> from the header and switch to the <span className="text-yellow-300">Awaiting Dispatch</span> tab. Items are sorted oldest-approved-first, each with a held-time chip.</>,
            <>On the row you've sent, click <span className="text-white">Mark Dispatched</span>, then pick the <span className="text-white">recipient</span>.</>,
            <>Enter the <span className="text-white">date sent</span> (the real date, even if you're recording it later).</>,
            <>Optionally add the channel and a note, then save.</>,
          ]}
        />
      </Section>

      <Section title="Every box explained">
        <Field name="Recipient" required="required">
          <p>Who or where the item was sent. Pick from the dropdown of upward recipients (EDGE Chairman, EDGE MD Office, Board / ExCom) or type a more specific destination if the form allows.</p>
          <p className="text-white/40">Examples: "EDGE MD Office", "Board Secretary (J. Mwangi)", "ExCom via Boardvantage portal".</p>
        </Field>

        <Field name="Date sent" required="required">
          <p>The actual date you dispatched the item (not today's date if you're recording it after the fact). Used to work out the working-day lag.</p>
          <p className="text-white/40">Cannot be in the future. Cannot be before the SDF MD approval date.</p>
        </Field>

        <Field name="Channel / Method" required="optional">
          <p>How you sent it — email, courier, hand-delivered, board portal, etc. Useful for the record but not mandatory.</p>
        </Field>

        <Field name="Dispatch note" required="optional">
          <p>Free text — anything else worth recording (e.g. "Sent to assistant for distribution to board members"). Recommended.</p>
        </Field>
      </Section>

      <NextStep>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>Status moves to <span className="text-indigo-300">Sent Upward</span>.</li>
          <li>The item leaves the Awaiting Dispatch tab.</li>
          <li>Event <span className="text-white">DISPATCHED_UPWARD</span> is recorded with recipient, date, channel, note, and the working-day lag.</li>
          <li>The SDF MD principal (and the team member named "SDF MD") is emailed.</li>
          <li>The KPI panel on Summary updates immediately with your dispatch lag.</li>
          <li>For above-MD lanes, closure follows automatically — no further action needed.</li>
        </ul>
      </NextStep>

      <Callout variant="warning" title="If the date is wrong">
        You can't edit a dispatch event after it's saved. If you recorded the wrong date, ask an admin to add a corrective progress note explaining the actual date — the original event stays in the record.
      </Callout>
    </div>
  );
}
